<template>
  <section class="page-wrap">
    <UiPageHero
      eyebrow="Thiết bị"
      title="Tiếp nhận thiết bị"
      description="Tạo mới, cập nhật và điều chuyển thiết bị về khoa/phòng sử dụng."
    >
    </UiPageHero>

    <div class="grid gap-4 2xl:grid-cols-[minmax(360px,480px)_minmax(0,1fr)]">
      <form class="panel-pad min-w-0" @submit.prevent="createDevice">
        <p class="mb-4 text-base font-bold text-ink text-left">
          Thông tin tiếp nhận
        </p>
        <div class="grid gap-4 md:grid-cols-2">
          <UiInput
            v-model="device.maThietBi"
            label="Mã thiết bị"
            class="md:col-span-2"
            required
          />
          <UiInput
            v-model="device.tenThietBi"
            label="Tên thiết bị"
            class="md:col-span-2"
            required
          />
          <UiInput v-model="device.model" label="Model" />
          <UiInput v-model="device.hangSanXuat" label="Hãng sản xuất" />
          <UiInput v-model="device.donViCungCap" label="Đơn vị cung cấp" />
          <UiInput
            :model-value="giaTriInput"
            label="Giá trị tiếp nhận"
            inputmode="numeric"
            @keydown="blockNonNumericKey"
            @paste="pasteNumericOnly($event, updateGiaTriTiepNhan)"
            @update:modelValue="updateGiaTriTiepNhan"
          />
          <UiInput
            v-model.number="yearInput"
            label="Năm tiếp nhận"
            type="number"
            min="1900"
            :max="currentYear"
          />
          <UiSelect
            v-model.number="selectedGroup"
            label="Nhóm thiết bị"
            @change="loadTypes"
          >
            <option :value="0">Chọn nhóm</option>
            <option v-for="item in groups" :key="item.id" :value="item.id">
              {{ item.tenNhomThietBi }}
            </option>
          </UiSelect>
          <UiSelect
            v-model.number="device.idLoaiThietBi"
            label="Loại thiết bị"
            @change="loadDevicesByType"
          >
            <option :value="0">Chọn loại</option>
            <option v-for="item in types" :key="item.id" :value="item.id">
              {{ item.tenLoaiThietBi }}
            </option>
          </UiSelect>
          <UiSelect v-model="device.loai" label="Loại">
            <option value="">Chọn loại</option>
            <option
              v-for="item in dsLoai"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </UiSelect>
        </div>
        <div class="mt-4 flex flex-wrap items-center gap-3">
          <UiButton
            type="submit"
            icon="add"
            :loading="saving"
            loading-text="Đang lưu..."
            :disabled="!canCreateDevice"
            >Tiếp nhận thiết bị</UiButton
          >
        </div>
      </form>

      <div class="panel-pad min-w-0">
        <div
          class="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
        >
          <p class="text-base font-bold text-ink">Danh sách thiết bị</p>
          <UiInput
            v-model="query"
            class="w-full md:w-64"
            placeholder="Tìm thiết bị..."
          />
        </div>
        <div
          class="table-wrap max-h-[620px]"
          :class="{ 'is-loading': listLoading }"
        >
          <div v-if="listLoading" class="table-loading-overlay">
            <span class="table-spinner"></span>
            <span>Đang tải dữ liệu...</span>
          </div>
          <table class="data-table min-w-[1480px]">
            <thead>
              <tr>
                <th>
                  <p @click="toggleSort('tenThietBi')">
                    Tên thiết bị {{ sortMark("tenThietBi") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleSort('maThietBi')">
                    Mã thiết bị {{ sortMark("maThietBi") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleSort('namTiepNhan')">
                    Năm tiếp nhận {{ sortMark("namTiepNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleSort('giaTriTiepNhan')">
                    Nguyên giá {{ sortMark("giaTriTiepNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleSort('giaTriHienTai')">
                    Giá trị còn lại {{ sortMark("giaTriHienTai") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleSort('tyLeSuDungConLai')">
                    Tỷ lệ sử dụng còn lại {{ sortMark("tyLeSuDungConLai") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleSort('khoaPhongTiepNhan')">
                    Khoa phòng tiếp nhận {{ sortMark("khoaPhongTiepNhan") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleSort('khoaPhongHienTai')">
                    Khoa phòng hiện tại {{ sortMark("khoaPhongHienTai") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleSort('khoaPhongChuyenGiao')">
                    Khoa phòng chuyển giao {{ sortMark("khoaPhongChuyenGiao") }}
                  </p>
                </th>
                <th>
                  <p @click="toggleSort('loai')">Loại {{ sortMark("loai") }}</p>
                </th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr
                v-for="item in paginatedDevices"
                :key="item.id"
                class="hover:bg-slate-50"
              >
                <td class="font-semibold text-ink">{{ item.tenThietBi }}</td>
                <td>{{ item.maThietBi || "-" }}</td>
                <td>{{ getReceiptYear(item.thoiGianTiepNhan) }}</td>
                <td>{{ formatCurrency(item.giaTriTiepNhan) }}</td>
                <td>{{ formatCurrency(item.giaTriHienTai) }}</td>
                <td>{{ getRemainingUsage(item) }}</td>
                <td>{{ item.khoaPhongTiepNhan || "-" }}</td>
                <td>{{ item.khoaPhongHienTai || "-" }}</td>
                <td>{{ item.khoaPhongChuyenGiao || "-" }}</td>
                <td>{{ item.loai || item.loaiThietBi || "-" }}</td>
                <td class="text-right">
                  <div class="inline-flex w-max justify-end gap-2">
                    <UiButton
                      variant="secondary"
                      size="sm"
                      icon="edit"
                      @click="openUpdate(item)"
                      >Cập nhật</UiButton
                    >
                    <UiButton
                      variant="secondary"
                      size="sm"
                      icon="transfer"
                      @click="openTransfer(item)"
                      >Chuyển</UiButton
                    >
                    <UiButton
                      variant="danger"
                      size="sm"
                      icon="delete"
                      @click="openDelete(item)"
                      >Xóa</UiButton
                    >
                  </div>
                </td>
              </tr>
              <tr v-if="!filteredDevices.length">
                <td colspan="11" class="py-10 text-center text-slate-500">
                  Chưa có thiết bị.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <UiPagination
          v-model="page"
          :total="filteredDevices.length"
          :page-size="pageSize"
        />
      </div>
    </div>

    <UiModal v-if="transferOpen">
      <form @submit.prevent="transferDevice">
        <div class="mb-4 flex items-start justify-between gap-3">
          <div>
            <p class="text-xl font-bold text-ink">Chuyển thiết bị</p>
            <p class="text-sm text-slate-500">{{ selectedDeviceName }}</p>
          </div>
          <UiButton variant="ghost" size="sm" icon="close" @click="transferOpen = false"
            >Đóng</UiButton
          >
        </div>
        <UiSelect
          v-model.number="transferDepartmentId"
          label="Khoa/phòng nhận"
          required
        >
          <option :value="0">Chọn khoa/phòng</option>
          <option v-for="item in departments" :key="item.id" :value="item.id">
            {{ item.tenKhoaPhong }}
          </option>
        </UiSelect>
        <div class="mt-4 flex justify-end gap-3">
          <UiButton variant="secondary" icon="cancel" @click="transferOpen = false"
            >Hủy</UiButton
          >
          <UiButton type="submit" icon="transfer" :disabled="!transferDepartmentId"
            >Xác nhận chuyển</UiButton
          >
        </div>
      </form>
    </UiModal>

    <UiModal v-if="updateOpen">
      <form v-if="updateDevice" @submit.prevent="submitUpdateDevice">
        <div class="mb-4 flex items-start justify-between gap-3">
          <div>
            <p class="text-xl font-bold text-ink">Cập nhật thiết bị</p>
            <p class="text-sm text-slate-500">{{ updateDevice.tenThietBi }}</p>
          </div>
          <UiButton variant="ghost" size="sm" icon="close" @click="updateOpen = false"
            >Đóng</UiButton
          >
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <UiInput
            v-model="updateDevice.maThietBi"
            label="Mã thiết bị"
            class="md:col-span-2"
            required
          />
          <UiInput
            v-model="updateDevice.tenThietBi"
            label="Tên thiết bị"
            class="md:col-span-2"
            required
          />
          <UiInput v-model="updateDevice.model" label="Model" required />
          <UiInput
            v-model="updateDevice.hangSanXuat"
            label="Hãng sản xuất"
            required
          />
          <UiInput
            v-model="updateDevice.donViCungCap"
            label="Đơn vị cung cấp"
            required
          />
          <UiInput
            :model-value="updateGiaTriInput"
            label="Giá trị tiếp nhận"
            inputmode="numeric"
            @keydown="blockNonNumericKey"
            @paste="pasteNumericOnly($event, updateGiaTriCapNhat)"
            @update:modelValue="updateGiaTriCapNhat"
          />
          <UiInput
            v-model.number="updateYearInput"
            label="Năm tiếp nhận"
            type="number"
            min="1900"
            :max="currentYear"
          />
          <UiSelect
            v-model.number="updateSelectedGroup"
            label="Nhóm thiết bị"
            @change="loadUpdateTypes"
          >
            <option :value="0">Chọn nhóm</option>
            <option v-for="item in groups" :key="item.id" :value="item.id">
              {{ item.tenNhomThietBi }}
            </option>
          </UiSelect>
          <UiSelect
            v-model.number="updateDevice.idLoaiThietBi"
            label="Loại thiết bị"
          >
            <option :value="0">Chọn loại</option>
            <option v-for="item in updateTypes" :key="item.id" :value="item.id">
              {{ item.tenLoaiThietBi }}
            </option>
          </UiSelect>
        </div>

        <div class="mt-4 flex justify-end gap-3">
          <UiButton variant="secondary" icon="cancel" @click="updateOpen = false"
            >Hủy</UiButton
          >
          <UiButton
            type="submit"
            icon="save"
            :loading="updating"
            loading-text="Đang cập nhật..."
            :disabled="!canUpdateDevice"
            >Cập nhật</UiButton
          >
        </div>
      </form>
    </UiModal>

    <UiModal v-if="deleteDeviceTarget">
      <form @submit.prevent="deleteDevice">
        <div class="mb-4 flex items-start justify-between gap-3">
          <div>
            <p class="text-xl font-bold text-ink">Xóa thiết bị</p>
            <p class="text-sm text-slate-500">{{ deleteDeviceName }}</p>
          </div>
          <UiButton variant="ghost" size="sm" icon="close" @click="closeDelete">
            Đóng
          </UiButton>
        </div>

        <p class="text-sm font-semibold text-slate-600">
          Bạn có chắc chắn muốn xóa thiết bị
          <span class="font-bold text-ink">{{ deleteDeviceName }}</span>
          không?
        </p>

        <div class="mt-4 flex justify-end gap-3">
          <UiButton
            type="button"
            variant="secondary"
            icon="cancel"
            @click="closeDelete"
          >
            Hủy
          </UiButton>
          <UiButton
            type="submit"
            variant="danger"
            icon="delete"
            :loading="deletingDevice"
          >
            Xác nhận xóa
          </UiButton>
        </div>
      </form>
    </UiModal>
  </section>
</template>

<script lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import QLTSRepository from "@/services/QLTSRepository";
import { ThietBi } from "@/models/thietBi";
import { NhomThietBi } from "@/models/nhomThietBi";
import { ChiTietLoaiThietBi } from "@/models/chiTietLoaiThietBi";
import { KhoaPhong } from "@/models/khoaPhong";
import {
  blockNonNumericKey,
  formatCurrency,
  formatIntegerInput,
  normalizeText,
  parseIntegerInput,
  pasteNumericOnly,
} from "@/utils/format";
import { getErrorMessage, useToast } from "@/utils/toast";
import {
  compareSortValues,
  nextSortState,
  sortIndicator,
  SortState,
} from "@/utils/tableSort";

const emptyDevice = (): ThietBi =>
  ({
    maThietBi: "",
    tenThietBi: "",
    thoiGianTiepNhan: new Date(),
    tyLeHaoMon: 0,
    model: "",
    hangSanXuat: "",
    donViCungCap: "",
    idLoaiThietBi: 0,
    giaTriTiepNhan: 0,
    thoiGianHaoMon: 0,
    idKhoaPhongHienTai: 0,
    khoaPhongHienTai: "",
    idKhoaPhongTiepNhan: 0,
    idNguoiChuyen: 0,
    idNguoiNhan: 0,
    khoaPhongTiepNhan: "",
    idKhoaPhongChuyenGiao: 0,
    khoaPhongChuyenGiao: "",
    idKhoaPhongTruocChuyenGiao: 0,
    khoaPhongTruocChuyenGiao: "",
    thoiGianChuyenGiao: new Date(),
    thoiGianCapNhat: new Date(),
    tiepNhan: true,
    id: 0,
    loaiThietBi: "",
    tuChoi: false,
    hoatDong: true,
    lyDo: "",
    nguoiTuChoi: "",
    nguoiNgungHoatDong: "",
    lyDoNgung: "",
    banGiao: false,
    loai: "",
  } as ThietBi);

export default {
  setup() {
    const device = ref<ThietBi>(emptyDevice());
    const currentYear = new Date().getFullYear();
    const yearInput = ref(currentYear);
    const giaTriInput = ref("");
    const groups = ref<NhomThietBi[]>([]);
    const types = ref<ChiTietLoaiThietBi[]>([]);
    const dsLoai = ref([
      { label: "Hành chính", value: "Hành chính" },
      { label: "Chuyên môn", value: "Chuyên môn" },
    ]);
    const departments = ref<KhoaPhong[]>([]);
    const devices = ref<ThietBi[]>([]);
    const selectedGroup = ref(0);
    const query = ref("");
    const page = ref(1);
    const pageSize = 10;
    const saving = ref(false);
    const { showToast } = useToast();
    const transferOpen = ref(false);
    const selectedDevice = ref<ThietBi | null>(null);
    const transferDepartmentId = ref(0);
    const activeListTypeId = ref(0);
    const updateOpen = ref(false);
    const updateDevice = ref<any>(null);
    const updateYearInput = ref(currentYear);
    const updateGiaTriInput = ref("");
    const updateSelectedGroup = ref(0);
    const updateTypes = ref<ChiTietLoaiThietBi[]>([]);
    const updating = ref(false);
    const listLoading = ref(false);
    const sortState = ref<SortState>({ key: "", direction: "asc" });
    const deleteDeviceTarget = ref<ThietBi | null>(null);
    const deletingDevice = ref(false);

    const mapDevicesResponse = (data: any) =>
      Array.isArray(data) ? data : data ? [data] : [];

    const loadDevicesByType = () => {
      if (!device.value.idLoaiThietBi) {
        devices.value = [];
        page.value = 1;
        activeListTypeId.value = 0;
        return;
      }
      listLoading.value = true;
      QLTSRepository.getListThietById(device.value.idLoaiThietBi)
        .then((res) => {
          devices.value = mapDevicesResponse(res.data);
          page.value = 1;
          activeListTypeId.value = device.value.idLoaiThietBi;
        })
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được danh sách thiết bị."),
            "error",
          );
        })
        .finally(() => (listLoading.value = false));
    };

    const loadTypes = () => {
      device.value.idLoaiThietBi = 0;
      devices.value = [];
      page.value = 1;
      if (!selectedGroup.value) {
        types.value = [];
        return;
      }
      QLTSRepository.getListChiTietLoaiThietBi(selectedGroup.value)
        .then((res) => (types.value = res.data || []))
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được danh sách loại thiết bị."),
            "error",
          );
        });
    };

    const updateGiaTriTiepNhan = (value: string | number) => {
      device.value.giaTriTiepNhan = parseIntegerInput(value);
      giaTriInput.value = formatIntegerInput(value);
    };

    const updateGiaTriCapNhat = (value: string | number) => {
      if (updateDevice.value) {
        updateDevice.value.giaTriTiepNhan = parseIntegerInput(value);
      }
      updateGiaTriInput.value = formatIntegerInput(value);
    };

    const selectedType = computed(() =>
      types.value.find((item) => item.id === device.value.idLoaiThietBi),
    );
    const selectedGroupName = computed(
      () =>
        groups.value.find((item) => item.id === selectedGroup.value)
          ?.tenNhomThietBi || "",
    );
    const selectedUpdateType = computed(() =>
      updateTypes.value.find(
        (item) => item.id === updateDevice.value?.idLoaiThietBi,
      ),
    );
    const selectedUpdateGroupName = computed(
      () =>
        groups.value.find((item) => item.id === updateSelectedGroup.value)
          ?.tenNhomThietBi || "",
    );
    const canCreateDevice = computed(
      () =>
        Boolean(device.value.maThietBi.trim()) &&
        Boolean(device.value.tenThietBi.trim()) &&
        Boolean(device.value.model.trim()) &&
        Boolean(device.value.hangSanXuat.trim()) &&
        Boolean(device.value.donViCungCap.trim()) &&
        Number(device.value.giaTriTiepNhan) > 0 &&
        Number(yearInput.value) >= 1900 &&
        Number(yearInput.value) <= currentYear &&
        Number(selectedGroup.value) > 0 &&
        Number(device.value.idLoaiThietBi) > 0 &&
        Boolean(device.value.loai.trim()),
    );
    const canUpdateDevice = computed(
      () =>
        Boolean(updateDevice.value?.maThietBi?.trim()) &&
        Boolean(updateDevice.value?.tenThietBi?.trim()) &&
        Boolean(updateDevice.value?.model?.trim()) &&
        Boolean(updateDevice.value?.hangSanXuat?.trim()) &&
        Boolean(updateDevice.value?.donViCungCap?.trim()) &&
        Number(updateDevice.value?.giaTriTiepNhan) > 0 &&
        Number(updateYearInput.value) >= 1900 &&
        Number(updateYearInput.value) <= currentYear &&
        Number(updateSelectedGroup.value) > 0 &&
        Number(updateDevice.value?.idLoaiThietBi) > 0,
    );

    const createDevice = () => {
      saving.value = true;
      const selectedTypeId = device.value.idLoaiThietBi;
      const payload = {
        tenThietBi: device.value.tenThietBi,
        maThietBi: device.value.maThietBi,
        model: device.value.model,
        hangSanXuat: device.value.hangSanXuat,
        donViCungCap: device.value.donViCungCap,
        giaTriTiepNhan: device.value.giaTriTiepNhan,
        thoiGianHaoMon: selectedType.value?.thoiGianHaoMon || 0,
        tyLeHaoMon: selectedType.value?.tyLeHaoMon || 0,
        idLoaiThietBi: device.value.idLoaiThietBi,
        thoiGianTiepNhan: new Date(yearInput.value, 0, 1),
        hoatDong: true,
        loai: device.value.loai,
      };
      QLTSRepository.createThietBi(payload)
        .then(() => {
          showToast("Đã tiếp nhận thiết bị.", "success");
          return QLTSRepository.getListThietById(selectedTypeId);
        })
        .then((res) => {
          devices.value = mapDevicesResponse(res.data);
          page.value = 1;
          activeListTypeId.value = selectedTypeId;
          device.value = emptyDevice();
          giaTriInput.value = "";
          selectedGroup.value = 0;
          yearInput.value = currentYear;
          types.value = [];
        })
        .catch((err) => {
          showToast(getErrorMessage(err, "Không lưu được thiết bị."), "error");
        })
        .finally(() => (saving.value = false));
    };

    const openTransfer = (item: ThietBi) => {
      selectedDevice.value = item;
      transferDepartmentId.value = 0;
      transferOpen.value = true;
    };

    const reloadActiveList = () => {
      const id = activeListTypeId.value || device.value.idLoaiThietBi;
      if (!id) return;
      listLoading.value = true;
      QLTSRepository.getListThietById(id)
        .then((res) => {
          devices.value = mapDevicesResponse(res.data);
          page.value = 1;
          activeListTypeId.value = id;
        })
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải lại được danh sách thiết bị."),
            "error",
          );
        })
        .finally(() => (listLoading.value = false));
    };

    const openUpdate = (item: ThietBi) => {
      updateDevice.value = {
        id: item.id,
        tenThietBi: item.tenThietBi || "",
        maThietBi: item.maThietBi || "",
        model: item.model || "",
        hangSanXuat: item.hangSanXuat || "",
        donViCungCap: item.donViCungCap || "",
        idLoaiThietBi: item.idLoaiThietBi || 0,
        giaTriTiepNhan: Number(item.giaTriTiepNhan || 0),
      };
      updateYearInput.value =
        Number(getReceiptYear(item.thoiGianTiepNhan)) || currentYear;
      updateGiaTriInput.value = formatIntegerInput(item.giaTriTiepNhan || 0);
      updateSelectedGroup.value =
        groups.value.find((group) => group.tenNhomThietBi === item.loai)?.id ||
        selectedGroup.value ||
        0;
      updateTypes.value = [];
      updateOpen.value = true;
      if (updateSelectedGroup.value) {
        QLTSRepository.getListChiTietLoaiThietBi(updateSelectedGroup.value)
          .then((res) => (updateTypes.value = res.data || []))
          .catch((err) => {
            showToast(
              getErrorMessage(err, "Không tải được danh sách loại thiết bị."),
              "error",
            );
          });
      }
    };

    const loadUpdateTypes = () => {
      if (!updateDevice.value) return;
      updateDevice.value.idLoaiThietBi = 0;
      updateTypes.value = [];
      if (!updateSelectedGroup.value) return;
      QLTSRepository.getListChiTietLoaiThietBi(updateSelectedGroup.value)
        .then((res) => (updateTypes.value = res.data || []))
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được danh sách loại thiết bị."),
            "error",
          );
        });
    };

    const submitUpdateDevice = () => {
      if (!updateDevice.value) return;
      updating.value = true;
      const payload = {
        id: updateDevice.value.id,
        tenThietBi: updateDevice.value.tenThietBi,
        maThietBi: updateDevice.value.maThietBi,
        thoiGianTiepNhan: new Date(updateYearInput.value, 0, 1),
        model: updateDevice.value.model,
        hangSanXuat: updateDevice.value.hangSanXuat,
        donViCungCap: updateDevice.value.donViCungCap,
        idLoaiThietBi: updateDevice.value.idLoaiThietBi,
        thoiGianHaoMon: selectedUpdateType.value?.thoiGianHaoMon || 0,
        tyLeHaoMon: selectedUpdateType.value?.tyLeHaoMon || 0,
        giaTriTiepNhan: updateDevice.value.giaTriTiepNhan,
        loai: selectedUpdateGroupName.value,
      };
      QLTSRepository.updateThietBi(payload as any)
        .then(() => {
          showToast("Đã cập nhật thiết bị.", "success");
          updateOpen.value = false;
          reloadActiveList();
        })
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không cập nhật được thiết bị."),
            "error",
          );
        })
        .finally(() => (updating.value = false));
    };

    const openDelete = (item: ThietBi) => {
      deleteDeviceTarget.value = item;
    };

    const closeDelete = () => {
      if (deletingDevice.value) return;
      deleteDeviceTarget.value = null;
    };

    const deleteDeviceName = computed(() =>
      deleteDeviceTarget.value ? deleteDeviceTarget.value.tenThietBi : "",
    );

    const deleteDevice = () => {
      if (!deleteDeviceTarget.value) return;
      deletingDevice.value = true;
      QLTSRepository.deleteThietBi(deleteDeviceTarget.value.id)
        .then(() => {
          showToast("Đã xóa thiết bị.", "success");
          deleteDeviceTarget.value = null;
          reloadActiveList();
        })
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không xóa được thiết bị."),
            "error",
          );
        })
        .finally(() => (deletingDevice.value = false));
    };

    const transferDevice = () => {
      if (!selectedDevice.value) return;
      QLTSRepository.tranferThietBi(
        selectedDevice.value.id,
        transferDepartmentId.value,
      )
        .then(() => {
          showToast("Đã chuyển thiết bị.", "success");
          transferOpen.value = false;
          reloadActiveList();
        })
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không chuyển được thiết bị."),
            "error",
          );
        });
    };

    const filteredDevices = computed(() => {
      const term = normalizeText(query.value);
      if (!term) return devices.value;
      return devices.value.filter((item) =>
        [
          item.tenThietBi,
          item.maThietBi,
          item.model,
          item.loai,
          item.loaiThietBi,
          item.khoaPhongHienTai,
          item.khoaPhongTiepNhan,
          item.khoaPhongChuyenGiao,
        ]
          .map(normalizeText)
          .some((value) => value.includes(term)),
      );
    });
    const getSortValue = (item: ThietBi, key: string) => {
      const map: Record<string, unknown> = {
        tenThietBi: item.tenThietBi,
        maThietBi: item.maThietBi,
        namTiepNhan: getReceiptYear(item.thoiGianTiepNhan),
        giaTriTiepNhan: item.giaTriTiepNhan,
        giaTriHienTai: item.giaTriHienTai,
        tyLeSuDungConLai: (item as any).tyLeSuDungConLai,
        khoaPhongTiepNhan: item.khoaPhongTiepNhan,
        khoaPhongHienTai: item.khoaPhongHienTai,
        khoaPhongChuyenGiao: item.khoaPhongChuyenGiao,
        loai: item.loai || item.loaiThietBi,
      };
      return map[key];
    };
    const sortedDevices = computed(() => {
      if (!sortState.value.key) return filteredDevices.value;
      return [...filteredDevices.value].sort((first, second) =>
        compareSortValues(
          getSortValue(first, sortState.value.key),
          getSortValue(second, sortState.value.key),
          sortState.value.direction,
        ),
      );
    });
    const paginatedDevices = computed(() => {
      const start = (page.value - 1) * pageSize;
      return sortedDevices.value.slice(start, start + pageSize);
    });
    watch(query, () => {
      page.value = 1;
    });
    watch(filteredDevices, () => {
      const maxPage = Math.max(
        1,
        Math.ceil(filteredDevices.value.length / pageSize),
      );
      if (page.value > maxPage) page.value = maxPage;
    });
    const getReceiptYear = (value?: string | Date | null) => {
      if (!value) return "-";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "-" : date.getFullYear();
    };
    const getRemainingUsage = (item: ThietBi) => {
      const receiptYear = Number(getReceiptYear(item.thoiGianTiepNhan));
      const depreciationYears = Number(item.thoiGianHaoMon || 0);
      if (!receiptYear || !depreciationYears) return "-";
      const usedYears = Math.max(0, currentYear - receiptYear);
      const remaining = Math.max(
        0,
        ((depreciationYears - usedYears) / depreciationYears) * 100,
      );
      return `${remaining.toFixed(2)}%`;
    };
    const toggleSort = (key: string) => {
      sortState.value = nextSortState(sortState.value, key);
      page.value = 1;
    };
    const sortMark = (key: string) => sortIndicator(sortState.value, key);
    const selectedDeviceName = computed(() =>
      selectedDevice.value ? selectedDevice.value.tenThietBi : "",
    );

    onMounted(() => {
      QLTSRepository.getListNhomThietBi()
        .then((res) => (groups.value = res.data || []))
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được danh sách nhóm thiết bị."),
            "error",
          );
        });
      QLTSRepository.getListKhoaPhong()
        .then((res) => (departments.value = res.data || []))
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được danh sách khoa/phòng."),
            "error",
          );
        });
    });

    return {
      device,
      yearInput,
      currentYear,
      giaTriInput,
      updateGiaTriTiepNhan,
      blockNonNumericKey,
      pasteNumericOnly,
      groups,
      types,
      dsLoai,
      departments,
      selectedGroup,
      loadTypes,
      createDevice,
      canCreateDevice,
      saving,
      query,
      filteredDevices,
      sortedDevices,
      paginatedDevices,
      page,
      pageSize,
      loadDevicesByType,
      formatCurrency,
      getReceiptYear,
      getRemainingUsage,
      toggleSort,
      sortMark,
      transferOpen,
      selectedDevice,
      selectedDeviceName,
      transferDepartmentId,
      openTransfer,
      transferDevice,
      updateOpen,
      updateDevice,
      updateYearInput,
      updateGiaTriInput,
      updateGiaTriCapNhat,
      updateSelectedGroup,
      updateTypes,
      updating,
      listLoading,
      canUpdateDevice,
      openUpdate,
      loadUpdateTypes,
      submitUpdateDevice,
      openDelete,
      closeDelete,
      deleteDevice,
      deleteDeviceTarget,
      deleteDeviceName,
      deletingDevice,
    };
  },
};
</script>
